import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatDialog, MatDialogConfig, MatSnackBar } from '@angular/material';
import { ActivatedRoute } from '@angular/router';
import { Cache } from 'aws-amplify';
import { MachineRouteService } from '../machine-route.service';
import { InventoryHttpService } from '../../../../core/inventory';
import { InventoryDialogComponent } from './inventory-dialog/inventory-dialog.component';
import { CameraHistoryDialogComponent, CameraHistoryDialogData } from './camera-history-dialog/camera-history-dialog.component';
import { MixpanelService } from '../../../../services/mixpanel.service';
import { FeatureFlagService } from '../../../../core/_services/feature-flag.service';
import { AuditApiService } from '../../camera-audit-live/core/audit-api.service';
import {
	CameraFillLevel,
	CameraFillLevels,
	cameraLevelsBySlot,
	describeLatestRestock,
	formatLocalDateTime,
	formatPct,
	formatReadingAge,
	isBrewVisionWorking,
	latestRestock,
	readShowCameraReadings,
	RestockComparison,
	writeShowCameraReadings
} from '../../../../core/machine-camera/_services/machine-camera.util';

@Component({
  selector: 'kt-inventory',
  templateUrl: './inventory.component.html',
  styleUrls: ['./inventory.component.scss']
})
export class InventoryComponent implements OnInit, OnDestroy {

	selectedMachine: any;

	beanSize = "0px";
	public bgkColor = "red";
	public height = "20%";
	inventory = [];
	user;
	role ;
	private dialogConfig = new MatDialogConfig();

	// Super admins on a machine with a camera can show its latest reading next to the machine's level.
	camera: CameraFillLevels = null;
	showCamera = false;
	// The per-canister History dialog is behind a feature flag for now.
	canSeeHistory = false;
	private cameraBySlot: { [slotNo: number]: CameraFillLevel } = {};
	// The last 30 days of restocks, for the "Restocked Oct 21" line under each canister. Super admins only.
	private restocks: RestockComparison[] = null;
	private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private machineRouteService: MachineRouteService,
    private cdr: ChangeDetectorRef,
    public dialog: MatDialog,
    public http: InventoryHttpService,
    private _snackBar: MatSnackBar,
    private mixPanelService: MixpanelService,
    private cameraApi: AuditApiService,
    private featureFlagService: FeatureFlagService
  ) {}

  ngOnInit() {
	this.selectedMachine = this.machineRouteService.getMachine(this.route);
	if (!this.selectedMachine) return;

	this.user = Cache.getItem('userAttr');
	this.role = this.user['custom:role'];

	this.getInventory();
	if (this.role === 'sadmin') {
		this.getCameraLevels();
		this.getRestocks();
		this.canSeeHistory = this.featureFlagService.hasFeatureFlag('canister_history');
	}

	// for(let i=0;i<this.inventory.length;i++){
	// 	if(this.inventory[i].name == "BEANS"){
	// 		beansObj = this.inventory[i];
	// 		this.inventory.slice(i, i+1);
	// 	}
	// }

	setTimeout(() => {
		this.beanSize = "350px";
		this.cdr.detectChanges();
	}, 1000);

	this.mixPanelService.track(this.mixPanelService.PAGE_VIEW, {
		'Machine ID': this.selectedMachine.machine_id,
		'Machine Name': this.selectedMachine.machine_name,
		'Machine Serial No': this.selectedMachine.serial_no
	});
  }

  getInventory(){
	this.http.getMachineInventory({machine_id: this.selectedMachine.machine_id})
	.subscribe((invData) => {
		console.log(invData);

		if(invData.length){
			this.inventory = invData;
			let beansObj;
			if(this.inventory[this.inventory.length-1].name == "BEANS") {
				beansObj = this.inventory.pop();
				this.inventory.unshift(beansObj);
			}

			this.cdr.detectChanges();
		}
	});

  }

  ngOnDestroy() {
	this.subscriptions.unsubscribe();
  }

  // The page works the same without it, so a failure only leaves the toggle out.
  getCameraLevels() {
	this.subscriptions.add(this.cameraApi.getMachineCameraFillLevels(this.selectedMachine.machine_id).subscribe(data => {
		if (!data || !data.camera) {
			return;
		}
		this.camera = data;
		this.cameraBySlot = cameraLevelsBySlot(data.levels);
		this.showCamera = readShowCameraReadings();
		this.cdr.detectChanges();
	}, () => {}));
  }

  // Like the camera levels, a failure only leaves the restock line out.
  getRestocks() {
	this.subscriptions.add(this.cameraApi.getMachineCameraRestocks(this.selectedMachine.machine_id, '30d').subscribe(data => {
		this.restocks = data ? data.restocks : null;
		this.cdr.detectChanges();
	}, () => {}));
  }

  lastRestockText(item): string {
	if (!this.restocks || !item) {
		return '';
	}
	return describeLatestRestock(latestRestock(this.restocks, item.slotNo, this.showCamera), '30 days');
  }

  onShowCameraChange(checked: boolean) {
	this.showCamera = checked;
	writeShowCameraReadings(checked);
	this.cdr.detectChanges();
  }

  cameraLevel(item): CameraFillLevel | null {
	if (!this.showCamera || !item) {
		return null;
	}
	const level = this.cameraBySlot[item.slotNo];
	return level && level.fill_pct !== null ? level : null;
  }

  // The fill is drawn in a container turned upside down, so top is measured from the bottom.
  // Kept just off the very bottom so an empty reading stays visible above the canister's base.
  cameraMarkerTop(level: CameraFillLevel): string {
	return Math.min(99, Math.max(2, level.fill_pct)) + '%';
  }

  cameraPct(level: CameraFillLevel): string {
	return formatPct(level.fill_pct);
  }

  cameraAge(level: CameraFillLevel): string {
	return formatReadingAge(level.measured_at_utc, new Date(), level.measured_at);
  }

  openCameraHistory(item) {
	const data: CameraHistoryDialogData = {
		machineId: this.selectedMachine.machine_id,
		slotNo: item.slotNo,
		name: item.name,
		showBrewVision: Boolean(this.camera && this.showCamera)
	};
	this.dialog.open(CameraHistoryDialogComponent, { data, width: '760px', maxWidth: '95vw', autoFocus: false });
  }

  cameraTooltip(item, level: CameraFillLevel): string {
	const when = `BrewVision reading from ${formatLocalDateTime(level.measured_at)}`;
	const reading = level.name && item.name && level.name.toUpperCase() !== String(item.name).toUpperCase()
		? `${when}. BrewVision has this canister as ${level.name}.`
		: `${when}.`;
	return reading;
  }

  isCameraWorking(): boolean {
	const camera = this.camera && this.camera.camera;
	return Boolean(camera) && isBrewVisionWorking(camera.status, camera.last_seen_utc);
  }

  cameraSeen(): string {
	const camera = this.camera && this.camera.camera;
	if (!camera) {
		return '';
	}
	if (this.isCameraWorking()) {
		return 'online';
	}
	const state = camera.status === 'online' ? 'not reporting' : 'offline';
	const age = formatReadingAge(camera.last_seen_utc, new Date(), camera.last_seen);
	return age ? `${state}, last seen ${age}` : state;
  }

  calcInvLvl(obj,intent){
	let perc = 0;
	if(intent == 'inv')
		perc = (obj.remSize/obj.canSize) * 100;
	else if(intent == 'low' && (obj.lowLevel != null || obj.lowLevel != undefined))
		perc = (obj.lowLevel/obj.canSize) * 100;
	else if(intent == 'empty' && (obj.emptyLevel != null || obj.emptyLevel != undefined))
		perc = (obj.emptyLevel/obj.canSize) * 100;
	else
		perc = 0;
	return (perc).toFixed(0)+"%";
  }

  getInventoryStatus(obj): string {
	const perc = (obj.remSize / obj.canSize) * 100;
	if (perc === 0) {
		return 'empty';
	} else if (perc <= 30) {
		return 'low';
	} else {
		return 'healthy';
	}
  }

  getStatusColorClass(status: string): string {
	switch (status) {
		case 'healthy':
			return 'inventory-status-chip--healthy';
		case 'low':
			return 'inventory-status-chip--low';
		case 'empty':
			return 'inventory-status-chip--empty';
		default:
			return 'inventory-status-chip--healthy';
	}
  }

  // onEdit(obj){
	// this.dialogConfig.autoFocus = true;
	// this.dialogConfig.data = obj;
	// const dialogRef = this.dialog.open(InventoryDialogComponent, this.dialogConfig);
	// 		dialogRef.afterClosed().subscribe(res => {
	// 			if (!res) {
	// 				return;
	// 			} else {
	// 				if(obj.canSize != res.par) {
	// 					obj.canSize = res.par;
	// 				}
	// 				if((obj.lowLevel/obj.canSize) * 100 != res.low_lvl){
	// 					obj.lowLevel = (res.low_lvl/100)*obj.canSize;
	// 				}
	// 				if((obj.emptyLevel/obj.canSize) * 100 != res.empty_lvl){
	// 					obj.emptyLevel = (res.empty_lvl/100)*obj.canSize;
	// 				}
	// 				if((obj.remSize/obj.canSize) * 100 != res.rem_lvl) {
	// 					obj.remSize = (res.rem_lvl/100)*obj.canSize;
	// 				}
	// 				this.http.updateMacInv(obj)
	// 				.then(res => {
	// 					if(res.success){
	// 						this.showSnackBar("Updated Successfully", 2000);
	// 					}else{
	// 						this.showSnackBar("Unable to Update", 0);
	// 					}
	// 				})
	// 				.catch(err => {
	// 					if(err.message){
	// 						this.showSnackBar(err.message, 0);
	// 					} else {
	// 						this.showSnackBar("An error occurred. Unable to Update!!!", 0);
	// 					}
	// 				});
	// 			}
	// 		});
  // }

  // showSnackBar(message: string, duration: number) {
	// 	let snackRef = this._snackBar.open(message, 'close');
	// 	if (duration > 0) {
	// 		setTimeout(() => {
	// 			this._snackBar.dismiss();
	// 		}, duration);
	// 	}

	// 	snackRef.onAction().subscribe(() => {
	// 		this._snackBar.dismiss();
	// 	});
	// }

}
